import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Text from '../components/shared/Text';

function Login() {
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        remember_me: false
    });
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [demoLoading, setDemoLoading] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev: any) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const response = await fetch('/api/v1/auth/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': (window as any).CSRF_TOKEN || document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
                },
                body: JSON.stringify({
                    email: formData.email,
                    password: formData.password
                })
            });

            const data = await response.json();

            if (response.ok && data.token) {
                // Store authentication data
                localStorage.setItem('token', data.token);
                localStorage.setItem('user', JSON.stringify(data.user));
                if (data.user.role) localStorage.setItem('user_role', data.user.role);
                
                // Set default firm for staff
                if (data.firms && data.firms.length > 0) {
                    localStorage.setItem('current_firm_id', data.firms[0].id);
                }

                // Role-based redirection
                if (data.user.role === 'customer') {
                    window.location.href = '/portal';
                } else if (data.user.role === 'staff' || data.user.role === 'admin') {
                    window.location.href = '/';
                } else {
                    window.location.href = '/';
                }
            } else {
                setError(data.error || 'Invalid email or password');
            }
        } catch (err) {
            setError('Login failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleDemoLogin = async () => {
        setDemoLoading(true);
        setError(null);

        try {
            const response = await fetch('/api/v1/auth/demo_login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();

            if (response.ok && data.token) {
                localStorage.setItem('token', data.token);
                localStorage.setItem('demo_mode', 'true');
                localStorage.setItem('user', JSON.stringify(data.user));
                window.location.href = '/';
            } else {
                setError(data.error || 'Demo login failed. Please try again.');
            }
        } catch (err) {
            setError('Demo login failed. Please try again.');
        } finally {
            setDemoLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex relative overflow-hidden">
            {/* Animated Background Shapes */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] right-[-5%] w-[40%] h-[40%] bg-purple-500/30 rounded-full blur-[100px] animate-pulse"></div>
                <div className="absolute bottom-[-10%] left-[-5%] w-[40%] h-[40%] bg-pink-500/30 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '1s' }}></div>
            </div>

            {/* Left Side - Form */}
            <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-20 xl:px-24 z-10 w-full max-w-lg mx-auto lg:max-w-none">
                <div className="mx-auto w-full max-w-sm lg:w-96">
                    <div className="text-center lg:text-left">
                        <Link to="/" className="inline-flex items-center space-x-2 mb-8 group">
                            <img src="/logo.png" alt="Inventory Mate" className="w-14 h-14 object-contain transform group-hover:scale-110 transition-transform" />
                            <Text variant="h3" color="white">Unified Portal</Text>
                        </Link>
                        <Text variant="h1" color="white" className="mb-4">
                            Portal Login
                        </Text>
                        <Text variant="body" color="white" className="opacity-80 text-lg">
                            Sign in to access your Orders, Deliveries, and Store.
                        </Text>
                    </div>

                    <div className="mt-10">
                        <div className="bg-white/10 backdrop-blur-xl border border-white/20 p-8 rounded-2xl shadow-2xl">
                            <form onSubmit={handleSubmit} className="space-y-6">
                                {error && (
                                    <div className="bg-red-500/20 border border-red-500/50 p-4 rounded-xl">
                                        <Text variant="small" weight="medium" className="text-red-200">{error}</Text>
                                    </div>
                                )}

                                <div>
                                    <Text as="label" variant="label" color="white" className="mb-2 block" htmlFor="email">
                                        Email Address
                                    </Text>
                                    <input
                                        id="email"
                                        name="email"
                                        type="email"
                                        autoComplete="email"
                                        required
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="block w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                        placeholder="you@company.com"
                                    />
                                </div>

                                <div>
                                    <Text as="label" variant="label" color="white" className="mb-2 block" htmlFor="password">
                                        Password
                                    </Text>
                                    <input
                                        id="password"
                                        name="password"
                                        type="password"
                                        autoComplete="current-password"
                                        required
                                        value={formData.password}
                                        onChange={handleChange}
                                        className="block w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                        placeholder="••••••••"
                                    />
                                </div>

                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <input
                                            id="remember_me"
                                            name="remember_me"
                                            type="checkbox"
                                            checked={formData.remember_me}
                                            onChange={handleChange}
                                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                                        />
                                        <Text as="label" variant="small" color="white" className="ml-2 opacity-70" htmlFor="remember_me">
                                            Remember me
                                        </Text>
                                    </div>

                                    <Link to="/forgot-password">
                                        <Text variant="small" weight="bold" className="text-purple-400 hover:text-purple-300 transition-colors">
                                            Forgot password?
                                        </Text>
                                    </Link>
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className={`w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-xl hover:from-purple-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-slate-900 transition-all shadow-lg shadow-purple-500/50 ${loading ? 'opacity-70 cursor-not-allowed' : 'hover:scale-105'}`}
                                >
                                    {loading ? (
                                        <div className="flex items-center justify-center gap-2">
                                            <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            <Text color="white" weight="bold">Signing in...</Text>
                                        </div>
                                    ) : (
                                        <Text color="white" weight="bold">Sign In</Text>
                                    )}
                                </button>

                                {/* Divider */}
                                <div className="relative">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-white/20"></div>
                                    </div>
                                    <div className="relative flex justify-center text-sm">
                                        <Text variant="small" color="white" className="px-4 bg-transparent opacity-50">Or</Text>
                                    </div>
                                </div>

                                {/* Demo Mode Button */}
                                <button
                                    type="button"
                                    onClick={handleDemoLogin}
                                    disabled={demoLoading}
                                    className={`w-full py-3 px-4 bg-white/10 border border-white/20 text-white font-bold rounded-xl hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all flex items-center justify-center gap-2 ${demoLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                                >
                                    {demoLoading ? (
                                        <>
                                            <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            <Text color="white" weight="bold">Loading Demo...</Text>
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                            <Text color="white" weight="bold">Try Demo Mode</Text>
                                        </>
                                    )}
                                </button>
                            </form>
                        </div>

                        <div className="mt-8 text-center">
                            <Text variant="small" color="white" className="opacity-70">
                                New to Inventory Pro?{' '}
                                <Link to="/signup">
                                    <Text as="span" weight="bold" className="text-purple-400 hover:text-purple-300 transition-colors">
                                        Create a free account
                                    </Text>
                                </Link>
                            </Text>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Side - Feature Panel */}
            <div className="hidden lg:block relative w-0 flex-1">
                <div className="absolute inset-0 flex flex-col justify-center p-20 text-white">
                    <div className="mb-10">
                        <span className="inline-block py-2 px-4 rounded-full bg-purple-500/20 border border-purple-400/30 text-purple-300 text-xs font-bold uppercase tracking-wider mb-6">
                            Version 2.0
                        </span>
                        <Text variant="h1" color="white" className="mb-6 leading-tight text-6xl">
                            Smart Inventory
                            <br />
                            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                                Simplified.
                            </span>
                        </Text>
                        <Text variant="body" color="white" className="opacity-80 text-xl max-w-xl leading-relaxed mb-10">
                            Experience the next generation of inventory management. Real-time tracking, predictive analytics, and effortless control.
                        </Text>

                        <div className="grid grid-cols-3 gap-4">
                            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/10">
                                <Text variant="h2" color="white" className="mb-2">99.9%</Text>
                                <Text variant="small" weight="bold" color="white" className="opacity-50 uppercase tracking-wider">Uptime</Text>
                            </div>
                            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/10">
                                <Text variant="h2" color="white" className="mb-2">10k+</Text>
                                <Text variant="small" weight="bold" color="white" className="opacity-50 uppercase tracking-wider">Users</Text>
                            </div>
                            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/10">
                                <Text variant="h2" color="white" className="mb-2">24/7</Text>
                                <Text variant="small" weight="bold" color="white" className="opacity-50 uppercase tracking-wider">Support</Text>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Login;
